/* eslint-disable import/no-extraneous-dependencies, arrow-body-style,object-curly-newline */
/* global React */
import { Grid } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { arrayOf, isObj } from 'lib/util'
import mapClasses from 'platform/react/hook/mapClasses'
import ScrollGrid from 'ui/Element/Grid/Scrollable'
import ErrorBoundary from 'ui/Error'

const prelogin = {
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'row',
  flexBasis: 'auto',
  maxWidth: '32rem !important',
  margin: 'auto',
}

/**
 * Section style decorators.
 *
 * Intended to be applied selectively through the {@link Section} and {@link Block} classes prop.
 * A classes prop should contain the names of the classes to be applied in each specific case.
 *
 * @param {Object} theme  application's styling theme
 * @returns {Object}       Section style decorators
 */
export const sectionStyles = makeStyles()(theme => ({
  start: {
    justifyContent: 'flex-start!important',
  },
  end: {
    justifyContent: 'flex-end!important',
  },
  center: {
    justifyContent: 'center!important',
  },
  spaceBetween: {
    justifyContent: 'space-between',
  },
  spaceAround: {
    justifyContent: 'space-around',
  },
  spaceEvenly: {
    justifyContent: 'space-evenly',
  },
  selfStart: {
    alignSelf: 'flex-start!important',
  },
  selfEnd: {
    alignSelf: 'flex-end!important',
  },
  selfCenter: {
    alignSelf: 'center!important',
  },
  selfStretch: {
    alignSelf: 'stretch!important',
  },
  itemsStart: {
    alignItems: 'flex-start!important',
  },
  itemsEnd: {
    alignItems: 'flex-end!important',
  },
  itemsCenter: {
    alignItems: 'center!important',
  },
  itemsBaseline: {
    alignItems: 'baseline!important',
  },
  itemsStretch: {
    alignItems: 'stretch!important',
  },
  contentsCenter: {
    alignContent: 'center!important',
  },
  contentsSpaceEvenly: {
    alignContent: 'space-evenly!important',
  },
  contentsEnd: {
    alignContent: 'flex-end!important',
  },
  textCenter: {
    textAlign: 'center',
  },
  separator: {
    borderBottom: '0.1875rem solid blue',
  },
  column: {
    flexDirection: 'column',
  },
  nowrap: {
    flexWrap: 'nowrap',
  },
  xsWrap: {
    // down() is exclusive, sm not included!
    [theme.breakpoints.down('sm')]: {
      flexWrap: 'wrap',
    },
  },
  smWrap: {
    // down() is exclusive, sm not included!
    [theme.breakpoints.down('md')]: {
      flexWrap: 'wrap',
    },
  },
  reverse: {
    flexFlow: 'wrap-reverse',
  },
  fill: {
    flex: '1 1 100%',
  },
  zeroMinHeight: {
    minHeight: 0,
  },
  prelogin: {
    ...prelogin,
    alignContent: 'center',
    justifyContent: 'center',
    minHeight: '100%',
    letterSpacing: 0,
    paddingTop: theme.spacing(5),
    paddingBottom: theme.spacing(5),
    [theme.breakpoints.down('sm')]: {
      alignContent: 'flex-start', // not vertically centered
      paddingTop: '1.5rem',
      paddingBottom: '1.5rem',
    },
  },
  preloginCard: {
    backgroundColor: theme.palette.background.main,
    borderRadius: theme.spacing(1.5),
    padding: theme.spacing(4),
    rowGap: theme.spacing(5),
    overflowWrap: 'anywhere',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
      rowGap: theme.spacing(2),
    },
  },
  wideLogin: {
    maxWidth: '55rem !important',
    '& *:not(input)': {
      textAlign: 'center !important',
    },
    '& > div > section > div > section > div': {
      maxWidth: '100%',
      justifyContent: 'center',
    },
    '& form': {
      marginTop: '1.2rem!important',
      flexBasis: '100%',
      [theme.breakpoints.up('md')]: {
        flexBasis: '58%',
      },
    },
    '& p': {
      width: '100%',
    },
  },
  secondaryLogin: {
    '& h6': {
      fontSize: '1.5rem',
      color: theme.palette.secondary.main,
    },
    '& button[type=submit]': {
      backgroundColor: theme.palette.secondary.main,
    },
  },
  register: {
    ...prelogin,
    justifyContent: 'flex-start',
    maxWidth: '50rem !important',
    marginTop: '1vw !important',
  },
  dashboard: {
    maxWidth: '41rem',
    maxHeight: 'auto',
    height: 'auto',
  },
  wideDashboard: {
    maxWidth: '46rem',
    maxHeight: 'auto',
    height: 'auto',
  },
  tallDashboard: {
    maxWidth: '41rem !important',
  },
  divider: {
    paddingTop: '2.188rem',
    borderTop: `0.0625rem solid ${theme.palette.divider}`,
  },
  borderLeft: {
    [theme.breakpoints.up('md')]: {
      borderLeftWidth: '0.0625rem!important',
    },
  },
  borderRight: {
    [theme.breakpoints.up('md')]: {
      borderRightWidth: '0.0625rem!important',
    },
  },
  borderTop: {
    borderTop: `0.0625rem solid ${theme.palette.divider}`,
  },
  borderBottom: {
    borderBottom: `0.0625rem solid ${theme.palette.divider}`,
  },
  paddingLeft: {
    [theme.breakpoints.up('sm')]: {
      paddingLeft: '2.375rem',
    },
  },
  marginLeft: {
    [theme.breakpoints.up('sm')]: {
      marginLeft: '2.375rem',
    },
  },
  flex: {
    flex: '1 1 0 !important',
    [theme.breakpoints.down('md')]: {
      flex: 'auto!important',
    },
  },
  wizard: {
    maxWidth: '56.25rem',
    [theme.breakpoints.down('md')]: {
      maxWidth: '100%',
    },
  },
  footer: {
    paddingLeft: theme.layout.navigationDrawerWidth,
    [theme.breakpoints.down('md')]: {
      paddingLeft: 0,
    },
    position: 'fixed!important',
    padding: theme.spacing(1),
    height: '3.75rem',
    // with absolute positioning, we can't use our regular borders
    borderTop: `0.0625rem solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.white.main,
    bottom: 0,
    left: 0,
    right: 0,
  },
  stickyBottom: {
    zIndex: 10,
    position: 'absolute!important',
    // with absolute positioning, we can't use our regular borders
    borderTop: `0.0625rem solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.white.main,
    bottom: 0,
    left: 0,
    right: 0,
  },
  stickyTop: {
    zIndex: 10,
    position: 'absolute!important',
    // with absolute positioning, we can't use our regular borders
    borderBottom: `0.0625rem solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.white.main,
    top: 0,
    left: 0,
    right: 0,
  },
  preface: {
    [theme.breakpoints.up('md')]: {
      paddingTop: '10vh',
    },
  },
  profile: {
    maxWidth: theme.breakpoints.values.md,
  },
  // visual classes
  bgGray: {
    backgroundColor: theme.palette.background.content,
  },
  bgWhite: {
    backgroundColor: theme.palette.background.main,
  },
  // Good for debugging
  bgRed: {
    backgroundColor: 'red',
  },
  bgBlue: {
    backgroundColor: 'blue',
  },
  bgGreen: {
    backgroundColor: 'green',
  },
  radius0_5: {
    borderRadius: theme.spacing(0.5),
  },
  radius1: {
    borderRadius: theme.spacing(1),
  },
  radius1_5: {
    borderRadius: theme.spacing(1.5),
  },
  radius2: {
    borderRadius: theme.spacing(2),
  },
  listBadge: {
    width: '1.5rem!important',
    height: '1.5rem!important',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.gray[950],
    borderRadius: '50%',
    padding: '0 !important',
  },
  ...theme.custom.section,
}))

/**
 * @typedef {Object} SectionProps
 * @property {string} [key]               element's key
 * @property {RefObject} [ref]            forwarded reference to the root element
 * @property {ReactNode} [children]       element's content
 * @property {Function} [iterator]        transformer function called for each children item
 * @property {string[]} [classes]         names of the styles among {@link styles} to be applied
 * @property {number} [scroll]            whether scroll is enabled
 * @property {number} [scrollBottom]      whether scroll is enabled and starts at the bottom
 * @property {number} [borderWidth]       element's border width
 * @property {string} [borderColor]       element's border color
 * @property {boolean} [fullHeight]       whether the element should have the parent's height
 * @property {boolean} [fullWidth]        whether the element should have the parent's width
 * @property {number} [padding]           size (spacing) of the padding area in all four sides
 * @property {number} [paddingHorizontal] left and right sizes (spacing) of the padding area
 * @property {number} [paddingVertical]   top and bottom sizes (spacing) of the padding area
 * @property {number} [paddingTop]        top size (spacing) of the padding area
 * @property {number} [paddingBottom]     bottom size (spacing) of the padding area
 * @property {number} [paddingLeft]       left size (spacing) of the padding area
 * @property {number} [paddingRight]      right size (spacing) of the padding area
 * @property {number} [margin]            size (spacing) of the margin area in all four sides
 * @property {number} [marginHorizontal]  left and right sizes (spacing) of the margin area
 * @property {number} [marginVertical]    top and bottom sizes (spacing) of the margin area
 * @property {number} [marginTop]         top size (spacing) of the margin area
 * @property {number} [marginBottom]      bottom size (spacing) of the margin area
 * @property {number} [marginLeft]        left size (spacing) of the margin area
 * @property {number} [marginRight]       right size (spacing) of the margin area
 * @property {number} [spacing]           amount of spacing around each child item
 * @property {number} [space]             amount of spacing above and below each child item
 * @property {number} [gap]               amount of spacing at the sides of each child item
 * @property {number} [grow]              factor by which to enlarge the item if there is space
 * @property {number} [shrink]            factor by which to shrink the item if needed
 * @property {number|'auto'} [xs]         the number of parent's columns used on xs width
 * @property {number|'auto'} [sm]         the number of parent's columns used on sm width
 * @property {number|'auto'} [md]         the number of parent's columns used on md width
 * @property {number|'auto'} [lg]         the number of parent's columns used on lg width
 * @property {number|'auto'} [xl]         the number of parent's columns used on xl width
 * @property {boolean} [hidden]           whether the element shouldn't be mounted at all
 */
const sectionProps = {
  key: '',
  classes: '',
  className: '',
  scroll: '',
  scrollBottom: '',
  borderWidth: 'border',
  borderColor: '',
  fullHeight: '',
  fullWidth: '',
  padding: '',
  paddingHorizontal: 'paddingX',
  paddingVertical: 'paddingY',
  paddingTop: '',
  paddingBottom: '',
  paddingLeft: '',
  paddingRight: '',
  margin: '',
  marginHorizontal: 'marginX',
  marginVertical: 'marginY',
  marginTop: '',
  marginBottom: '',
  marginLeft: '',
  marginRight: '',
  spacing: '',
  space: 'rowSpacing',
  gap: 'columnSpacing',
  grow: 'flexGrow',
  shrink: 'flexShrink',
  overflow: '',
  xs: '',
  sm: '',
  md: '',
  lg: '',
  xl: '',
  hidden: '',
}

/**
 * Searches for all {@link SectionProps} properties that can be found in `props` and groups them
 * together. Returns an array whose first element is an object containing all matching properties
 * and the second one contains the rest.
 *
 * If `map` is `true`, the first object of the returned array will have the names of its properties
 * mapped according to the values of the properties in {@link SectionProps}.
 *
 * @param {SectionProps & any} props  an object from which to extract {@link SectionProps}.
 * @param {boolean} [map]             whether to map the props names
 * @returns {[Object, Object]}         an array containing a first object all props from `props` that
 *                                    match those from {@link SectionProps}, and a second object
 *                                    with the rest
 */
const getItemProps = (props, map) => Object.keys(props).reduce(([itemProps, rest], key) => {
  const target = key in sectionProps ? itemProps : rest
  const targetKey = map ? sectionProps[key] || key : key
  target[targetKey] = props[key]
  return [itemProps, rest]
}, [{}, {}])

/**
 * Returns an array whose first object contains the properties from `props` that should be applied
 * to a child's wrapping {@link Block} and the second one the ones that should be applied to the
 * child item itself.
 *
 * By default, it attempts to create a new object with all properties from `props` that can be found
 * in {@link SectionProps} and returns it as the first item of the array and the rest in an object
 * as the second one.
 *
 * If there are any `props`children}, the element to whom the `props` belong is considered to be a
 * {@link Section} and then, in that case, the filtered {@link SectionProps} are returned inside the
 * second object of the array instead of the first one, so that the supposed child {@link Section}
 * receives them instead of its wrapping {@link Block}. This can be opted out by passing
 * `props.block` with `true`, in which case it will behave as it does by default.
 *
 * If `props.block` is an object, this function will attempt to obtain the {@link SectionProps} from
 * it instead and consider that the rest of `props` belong to the child item.
 *
 * @param {SectionProps & any} props          an object from which to extract {@link SectionProps}.
 * @param {SectionProps & any} [props.block]  an object from which to extract {@link SectionProps}.
 * @returns {[Object, Object]}
 */
const getBlockProps = (props) => {
  const { block, children, ...restProps } = props
  const blockIsObject = isObj(block)
  const targetProps = blockIsObject ? block : restProps
  const [gridItemProps, rest] = getItemProps(targetProps)
  const restItemProps = blockIsObject ? restProps : rest

  // const isBlock = blockIsObject ? block.block : block
  const isContainer = !block && !!children?.length
  const { fullWidth, fullHeight } = gridItemProps
  const { scroll, scrollBottom, ...gridBlockProps } = gridItemProps
  const blockProps = isContainer ? {} : gridBlockProps
  const itemBlockProps = isContainer ? gridItemProps : {}

  const itemProps = {
    ...itemBlockProps,
    ...restItemProps,
    ...fullWidth && { fullWidth },
    ...fullHeight && { fullHeight },
    children,
  }

  return [blockProps, itemProps]
}

/**
 * Item element used by {@link Section} and {@link Block} that abstracts the handling of all their
 * shared props and functionalities.
 *
 * It maps most props according to the values of {@link SectionProps} and passes them to an
 * underlying {@link Grid} or {@link ScrollGrid} component.
 *
 * @param {Object} props          element properties
 * @param {React.Ref} ref         forwarded reference of the root element
 * @param {React.Node[]} children element's content
 * @param {boolean} container     whether the element should behave as a `Grid` container
 * @param {boolean} hidden        whether the element is removed from the view
 */
const Item = ({ ref, children, component, sx, hidden, ...props }) => {
  const [itemProps] = hidden ? [{}] : getItemProps(props, true)
  const { scroll, scrollBottom, classes, fullWidth, fullHeight, ...rootProps } = itemProps || {}

  const scrollable = scroll || scrollBottom
  const Root = scrollable ? ScrollGrid : Grid
  const scrollProps = scrollable && scrollBottom ? { scrollProps: { bottom: scrollBottom } } : {}

  return hidden ? null : (
    <Root
      flexGrow={(scrollable && 1)}
      border={0}
      borderColor={'gray.900'}
      alignItems={'flex-start'}
      alignContent={'flex-start'}
      {...rootProps}
      {...scrollProps}
      container
      item
      className={[
        props.className,
        mapClasses(classes, sectionStyles),
      ].filter(item => !!item).join(' ')}
      ref={ref}
      component={component}
      noValidate={component === 'form'}
      sx={{
        ...fullWidth && { width: '100%' },
        ...fullHeight && { height: '100%' },
        ...sx,
      }}
    >
      {children}
    </Root>
  )
}

/**
 * Wraps the `child` element with a {@link Grid} element and sets the layout props
 * of the former and `options` as props of the latter.
 *
 * @param {Object} child                  the child element
 * @param {Object} child.key              child's identification key
 * @param {SectionProps} child.props      child component's props
 * @param {Object} options                child element options
 * @param {number} [options.segments={}]  the number of spacings between around the item
 * @returns {*}
 */
const Block = ({ child, options }) => {
  const { segments = {} } = options
  const { ...item } = child // shallow copying child, so that we can rewrite its props
  const { key, props } = item
  const { hidden } = props
  const [blockProps, itemProps] = hidden ? [{}, {}] : getBlockProps(props)
  const { scroll, scrollBottom } = itemProps || {}
  // setting child props after having filtered out the ones that belong to its wrapper
  item.props = { ...itemProps, ...!segments && { segments } }

  return hidden ? null : (
    <ErrorBoundary key={key}>
      <Item
        {...blockProps}
        key={key}
        sx={{
          ...(scroll || scrollBottom) && { overflow: 'auto' },
        }}
      >
        {item}
      </Item>
    </ErrorBoundary>
  )
}

/**
 * Helper function that maps each item of `children` to new a {@link Template} element.
 *
 * @param {Object} children[] the items to wrap each with a `Template` element
 * @param {function} Template an element to be used to represent each item from `children`
 * @param {Object} options    properties from {@link Section} that should affect its children
 * @returns {*}
 */
export const decorateAsBlockGrid = (children, { Template, ...options } = {}) => arrayOf(children)
  .filter(child => !!child)
  .map(child => Template({ child, options: { ...options } }))

/**
 * High-order section component designed to place and arrange other elements inside it, which will
 * be also automatically wrapped by another component to facilitate their placement. The arrangement
 * of these elements depends on their number and the width they occupy. This component's classes,
 * spacing, space, gap and width (xs, sm, md, lg and xl) properties offer additional possibilities.
 *
 * @param {SectionProps} [props]                element's properties
 * @param {string} [props.component='section']  the type of HTMLElement to use for the component
 * @param {React.Ref} [props.ref]               a reference object to the root element
 * @param {React.Node[]} [props.children]       element's content
 * @param {function} [props.iterator]           to loop through the content before displaying it
 * @returns {JSX.Element}                       the new component
 */
const Section = (props) => {
  const { ref, component, children, iterator, ...restProps } = props
  const [itemProps] = getItemProps(restProps)

  const decorate = iterator || decorateAsBlockGrid
  const items = decorate(children, { Template: Block })

  return (
    <Item
      {...itemProps}
      ref={ref}
      component={component || 'section'}
    >
      {items}
    </Item>
  )
}

export default Section
